import type { Node as ProseMirrorNode, Schema } from 'prosemirror-model'
import { DOMParser as ProseMirrorDOMParser, DOMSerializer } from 'prosemirror-model'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { artifactMarkdownParser } from '../custom_markdown_blocks/pleading-caption'
import { replaceAgentV2ArtifactCitationsWithSpans } from './artifact-citation-helpers'
import { sanitizeAgentV2ArtifactEditorHtml } from './sanitize-agent-v2-artifact-editor-html'
import { turndownService } from './turndown'

const escapedReferenceTokenPattern = /\\\[ref\\_([a-zA-Z0-9_-]+)\\\]/g

/**
 * Parse Markdown into a document owned by the supplied ProseMirror schema. `referencesOpenInPanel`
 * says whether a citation click opens the draft workspace's chat panel or a new browser tab.
 */
export function parseMarkdownDocument(
  schema: Schema,
  markdown: string,
  referencesByCitationId?: Record<string, AgentV2Reference>,
  { referencesOpenInPanel = false }: { referencesOpenInPanel?: boolean } = {}
): ProseMirrorNode {
  const markdownWithCitationSpans = replaceAgentV2ArtifactCitationsWithSpans(markdown, referencesByCitationId, { opensInPanel: referencesOpenInPanel })
  const rawHtml = artifactMarkdownParser.parse(markdownWithCitationSpans) as string
  const sanitizedHtml = sanitizeAgentV2ArtifactEditorHtml(rawHtml)
  const container = document.createElement('div')
  container.innerHTML = sanitizedHtml
  return ProseMirrorDOMParser.fromSchema(schema).parse(container)
}

/** Serialize a ProseMirror document as GFM Markdown. */
export function serializeMarkdownDocument(schema: Schema, proseMirrorDocument: ProseMirrorNode): string {
  const serializer = DOMSerializer.fromSchema(schema)
  const div = document.createElement('div')
  div.appendChild(serializer.serializeFragment(proseMirrorDocument.content))
  return turndownService.turndown(div.innerHTML).replace(escapedReferenceTokenPattern, (_match, referenceId: string) => `[ref_${referenceId}]`)
}
